"use client";

import React from "react";
import Link from "next/link";
import { ContentCard } from "fansunited-sports-ui";
import { LinkPagination } from "@/components/ui/LinkPagination";
import { BrandedHeader } from "@/components/branded/headers/BrandedHeader";
import type { ResolvedHeaderColors } from "@/features/theme/resolveBrandedStyle";
import { AdSlot } from "@/features/ads/AdSlot";
import { useLabels } from "@/lib/labels/LabelsProvider";
import { useNavigation } from "@/lib/navigation/NavigationProvider";
import type { Category, Post } from "@/payload-types";
import { getMediaFieldUrl } from "@/utilities/media/getMediaUrl";
import type { LocaleCode } from "@/lib/labels/types";
import type { CategoryPostType } from "../../data/registry";

interface CategoryDefaultProps {
    category: Category;
    posts: Post[];
    currentPage: number;
    totalPages: number;
    locale: LocaleCode;
    headerColors?: ResolvedHeaderColors;
    breadcrumbs?: React.ReactNode;
    activeType?: CategoryPostType;
}

type FilterKey = "all" | CategoryPostType;

export function CategoryDefault({
    category,
    posts,
    currentPage,
    totalPages,
    locale,
    headerColors,
    breadcrumbs,
    activeType,
}: CategoryDefaultProps) {
    const labels = useLabels();
    const urlBuilder = useNavigation();

    const withTypeQuery = (path: string) => (activeType ? `${path}?type=${activeType}` : path);

    const getPageHref = (page: number) =>
        withTypeQuery(page === 1 ? urlBuilder.category(category.slug!) : urlBuilder.categoryPage(category.slug!, page));

    const formatDate = (dateString: string | null | undefined) => {
        if (!dateString) return "";
        const date = new Date(dateString);
        return date.toLocaleDateString(locale, { day: "numeric", month: "short", year: "numeric" });
    };

    const getImageUrl = (heroImage: Post["heroImage"]): string | undefined => {
        return getMediaFieldUrl(heroImage, "medium") ?? undefined;
    };

    const filters: { key: FilterKey; href: string; label: string }[] = [
        {
            key: "all",
            href: urlBuilder.category(category.slug!),
            label: labels.category.filters?.all ?? "All",
        },
        {
            key: "article",
            href: `${urlBuilder.category(category.slug!)}?type=article`,
            label: labels.category.filters?.articles ?? "Articles",
        },
        {
            key: "video",
            href: `${urlBuilder.category(category.slug!)}?type=video`,
            label: labels.category.filters?.videos ?? "Videos",
        },
    ];

    const activeFilter: FilterKey = activeType ?? "all";
    const emptyStateLabel =
        activeType === "video" ? (labels.category.emptyStateVideos ?? labels.category.emptyState) : labels.category.emptyState;

    return (
        <div className="min-h-screen">
            <BrandedHeader
                title={category.title}
                titleAs="h1"
                subtitle={category.description ?? ""}
                colors={headerColors?.colors}
                backgroundImageUrl={headerColors?.backgroundImageUrl}
            />

            {breadcrumbs}

            <div className="max-w-350 mx-auto px-4 py-8 space-y-8">
                <AdSlot id="content-top" />

                <div className="flex flex-wrap gap-2">
                    {filters.map((filter) => {
                        const isActive = filter.key === activeFilter;
                        return (
                            <Link
                                key={filter.key}
                                href={filter.href}
                                prefetch={false}
                                className={`inline-flex items-center rounded-full border px-4 py-1.5 text-sm font-medium transition-colors ${
                                    isActive
                                        ? "bg-primary text-primary-foreground border-primary"
                                        : "border-border text-foreground hover:bg-muted"
                                }`}
                                aria-current={isActive ? "page" : undefined}
                            >
                                {filter.label}
                            </Link>
                        );
                    })}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {posts.map((post) => (
                        <ContentCard
                            key={post.id}
                            content={{
                                id: String(post.id),
                                type: post.type === "video" ? "video" : "article",
                                link: urlBuilder.post(post),
                                title: post.title,
                                description: post.summary || "",
                                image: {
                                    url: getImageUrl(post.heroImage) || "",
                                    alt: post.title,
                                },
                                metadata: {
                                    time: formatDate(post.publishedAt),
                                },
                            }}
                            variant="standard"
                            styles={{
                                typography: {
                                    title: "text-lg font-semibold",
                                    description: "text-sm text-muted-foreground line-clamp-3",
                                },
                            }}
                        />
                    ))}
                </div>

                {posts.length === 0 && (
                    <div className="text-center py-12 text-muted-foreground">{emptyStateLabel}</div>
                )}

                {totalPages > 1 && (
                    <div className="mt-8 flex justify-center">
                        <LinkPagination currentPage={currentPage} totalPages={totalPages} getPageHref={getPageHref} />
                    </div>
                )}

                <AdSlot id="content-bottom" />
            </div>
        </div>
    );
}
